/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo, useCallback } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { ObjectData, ObjectTileData } from '../types';

export type ObjectCellProps = {
	object: ObjectData | ObjectTileData;
	testId?: string;
	onObjectClick?: (object: ObjectData | ObjectTileData) => void;
	isSelected?: boolean;
	isObjectTile?: boolean;
};

const styles = cssMap({
	button: {
		display: 'flex',
		width: '120px',
		height: 'auto',
		paddingInline: token('space.100'),
		paddingBlock: token('space.100'),
		alignItems: 'center',
		justifyContent: 'space-between',
		flexDirection: 'column',
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text'),
		borderRadius: token('radius.xsmall'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: 'transparent',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	buttonSelected: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.selected'),

		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:focus-visible': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
});

/**
 * __Object cell__
 *
 * A single cell which is used in the grid of objects.
 * Contains the object, click/hover behaviour, and tooltip.
 */
const ObjectCell: React.NamedExoticComponent<ObjectCellProps> = memo(function ObjectCell({
	testId,
	isSelected,
	object,
	onObjectClick,
	isObjectTile,
}: ObjectCellProps) {
	const onButtonClick = useCallback(
		(evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
			evt.stopPropagation();
			onObjectClick?.(object);
		},
		[object, onObjectClick],
	);

	const objectElement = isObjectTile ? (
		<object.component label="" size="small" />
	) : (
		<object.component label="" size="medium" />
	);

	return (
		<span data-testid={testId}>
			<Pressable
				xcss={cx(styles.button, isSelected && styles.buttonSelected)}
				onClick={(evt) => {
					onButtonClick(evt);
				}}
			>
				<Stack space="space.100">
					<Flex justifyContent="center">{objectElement}</Flex>
					<Text size="small" color={isSelected ? 'color.text.selected' : 'color.text'}>
						{object.objectName}
					</Text>
				</Stack>
			</Pressable>
		</span>
	);
});

export default ObjectCell;
